import {
  rooms,
  WASH_ACTIVATION_STATUS_LABELS,
  WASH_APPOINTMENT_STATUS_LABELS,
  WASH_CREDIT_STATUS_LABELS,
  WASH_CYCLE_STATUS_LABELS,
  WASH_READY_SMS_STATUS_LABELS,
  WASH_UNIT_STATE_LABELS,
  WASH_WORK_ORDER_STATUS_LABELS,
  type WashActivationStatus,
  type WashAppointmentStatus,
  type WashCreditStatus,
  type WashCycleStatus,
  type WashReadySmsStatus,
  type WashUnitState,
  type WashWorkOrderStatus,
} from "@station/contracts";
import {
  Alert,
  type BreadcrumbItem,
  EmptyState,
  FormField,
  PageHeader,
  Select,
  SkeletonRows,
  StatusBadge,
} from "@station/ui";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Droplets, MapPinOff } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { errorMessage } from "../../lib/errors.js";
import { useDocumentTitle } from "../../lib/hooks.js";
import { useRoom } from "../../lib/realtime-context.js";
import { useScope } from "../../lib/scope.js";
import { useTenantShape } from "../../lib/tenant-shape.js";
import { useStationScope } from "../cashflow/shared.js";
import {
  useWashPermissions,
  useWashStations,
  type WashPermissions,
  type WashStationOption,
} from "./queries.js";
import {
  ACTIVATION_STATUS_TONE,
  APPOINTMENT_STATUS_TONE,
  CREDIT_STATUS_TONE,
  CYCLE_STATUS_TONE,
  READY_SMS_TONE,
  UNIT_STATE_TONE,
  WORK_ORDER_STATUS_TONE,
} from "./wash-values.js";

/** Yıkama ekranlarının ortak arama parametresi: istasyon (`?station=`). */
export const washSearchShape = {
  station: z.string().uuid().optional().catch(undefined),
};

export interface WashStationSelection {
  stations: WashStationOption[];
  station: WashStationOption | null;
  /** Tek istasyonlu kiracıda ve görünür tek yıkama istasyonunda seçici çizilmez (AGENTS.md §2). */
  showPicker: boolean;
  isLoading: boolean;
  error: unknown;
  select: (stationId: string) => void;
}

/**
 * Yıkama İSTASYON bazındadır: yalnızca WASH iş birimi etkin ve kullanıcının kapsamındaki istasyonlar seçilebilir. Seçim
 * URL'de taşınır (`?station=`); yoksa üst çubuktaki kapsam (tek istasyon seçiliyse) ya da ilk istasyon kullanılır.
 */
export function useWashStation(enabled = true): WashStationSelection {
  const stations = useWashStations(enabled);
  const scope = useStationScope();
  const topbarScope = useScope();
  const shape = useTenantShape();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { station?: string };
  const visible = (stations.data ?? []).filter(
    (station) => !scope.restricted || scope.allowedStationIds.includes(station.id),
  );
  const fromScope =
    topbarScope.kind === "station"
      ? visible.find((station) => station.id === topbarScope.stationId)
      : undefined;
  const selected =
    visible.find((station) => station.id === search.station) ?? fromScope ?? visible[0] ?? null;
  return {
    stations: visible,
    station: selected,
    showPicker: visible.length > 1 && !shape.isSingleStation,
    isLoading: stations.isPending && enabled,
    error: stations.error,
    select: (stationId) =>
      void navigate({
        to: ".",
        search: ((previous: Record<string, unknown>) => ({ ...previous, station: stationId })) as never,
        replace: true,
      }),
  };
}

export function WashStationField({
  selection,
  className = "w-64",
}: {
  selection: WashStationSelection;
  className?: string;
}): ReactElement | null {
  const { t } = useTranslation();
  if (!selection.showPicker) return null;
  return (
    <FormField label={t("wash.fields.station")} className={className}>
      {(control) => (
        <Select
          {...control}
          value={selection.station?.id ?? ""}
          onChange={(event) => selection.select(event.target.value)}
        >
          {selection.stations.map((station) => (
            <option key={station.id} value={station.id}>
              {station.code} {station.name}
            </option>
          ))}
        </Select>
      )}
    </FormField>
  );
}

export interface WashStationContext {
  station: WashStationOption;
  selection: WashStationSelection;
  permissions: WashPermissions;
}

type Requirement = "read" | "manage" | "operate" | "reports";

function hasRequirement(permissions: WashPermissions, requirement: Requirement): boolean {
  if (requirement === "manage") return permissions.canManage;
  if (requirement === "operate") return permissions.canOperate;
  if (requirement === "reports") return permissions.canReadReports;
  return permissions.canRead;
}

/** Bayrak ve izin kapısı (sayfa çerçeveleri ortak kullanır). */
function WashGate({
  requirement,
  children,
}: {
  requirement: Requirement;
  children: ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const permissions = useWashPermissions();
  if (!permissions.enabled)
    return (
      <EmptyState
        headingLevel={2}
        icon={<Droplets />}
        title={t("wash.disabledTitle")}
        description={t("wash.disabledDescription")}
      />
    );
  if (!permissions.canRead || !hasRequirement(permissions, requirement))
    return <Alert tone="info">{t(`wash.noPermission.${requirement}`)}</Alert>;
  // Izgara öğeleri içerikleri kadar genişlemesin (grafik tuvali, geniş tablo): tablet genişliğinde sayfa yatay kaymaz.
  return <div className="grid min-w-0 gap-4 [&>*]:min-w-0">{children}</div>;
}

/**
 * KİRACI düzeyindeki yıkama ekranları (programlar, ayarlar): başlık + kırıntı ("Yıkama › …") + bayrak / izin kapısı.
 */
export function WashPage({
  title,
  description,
  breadcrumb,
  actions,
  requirement = "read",
  children,
}: {
  title: string;
  description?: string | undefined;
  breadcrumb?: BreadcrumbItem[];
  actions?: ReactNode;
  requirement?: Requirement;
  children: ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const permissions = useWashPermissions();
  useDocumentTitle(title);
  const open = permissions.enabled && permissions.canRead && hasRequirement(permissions, requirement);
  return (
    <>
      <PageHeader
        title={title}
        {...(description ? { description } : {})}
        breadcrumb={[{ label: t("menu.groups.wash") }, ...(breadcrumb ?? [{ label: title }])]}
        actions={open ? actions : null}
      />
      <WashGate requirement={requirement}>{children}</WashGate>
    </>
  );
}

/**
 * İSTASYON düzeyindeki yıkama ekranları: başlıkta istasyon seçici; istasyon odasına abone olunur (ünite durumu, döngü,
 * iş emri olayları önbelleği tazeler). İstasyon değişince sayfanın yerel durumu sıfırlanır.
 */
export function WashStationPage({
  title,
  description,
  breadcrumb,
  actions,
  requirement = "read",
  children,
}: {
  title: string;
  description?: string | undefined;
  breadcrumb?: BreadcrumbItem[];
  actions?: (context: WashStationContext) => ReactNode;
  requirement?: Requirement;
  children: (context: WashStationContext) => ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const permissions = useWashPermissions();
  useDocumentTitle(title);
  const open = permissions.enabled && permissions.canRead && hasRequirement(permissions, requirement);
  const selection = useWashStation(open);
  const station = selection.station;
  useRoom(open && station ? rooms.station(station.id) : null);
  const context = station ? { station, selection, permissions } : null;
  return (
    <>
      <PageHeader
        title={title}
        {...(description ? { description } : {})}
        breadcrumb={[{ label: t("menu.groups.wash") }, ...(breadcrumb ?? [{ label: title }])]}
        actions={
          open ? (
            // Seçici (etiket + kutu) ile düğmeler ALT çizgide hizalanır.
            <div className="flex flex-wrap items-end gap-2">
              <WashStationField selection={selection} />
              {context && actions ? actions(context) : null}
            </div>
          ) : null
        }
      />
      <WashGate requirement={requirement}>
        {selection.error ? (
          <Alert tone="danger">{errorMessage(selection.error)}</Alert>
        ) : selection.isLoading ? (
          <SkeletonRows rows={6} />
        ) : context === null ? (
          <EmptyState
            headingLevel={2}
            icon={<MapPinOff />}
            title={t("wash.noStationsTitle")}
            description={t("wash.noStationsDescription")}
            action={
              <Link to="/ayarlar/istasyonlar" className="text-sm underline underline-offset-4">
                {t("menu.stations")}
              </Link>
            }
          />
        ) : (
          <div key={context.station.id} className="grid min-w-0 gap-4 [&>*]:min-w-0">
            {children(context)}
          </div>
        )}
      </WashGate>
    </>
  );
}

/** Kırıntıda bir yıkama listesine geri bağlantı (ayrıntı sayfaları). */
export function washCrumb(
  label: string,
  to: "/yikama/krediler" | "/yikama/is-emirleri",
  search?: Record<string, string>,
): BreadcrumbItem {
  return {
    label,
    link: (children, className) => (
      <Link to={to} search={(search ?? {}) as never} className={className}>
        {children}
      </Link>
    ),
  };
}

// ── Rozetler (anlam yalnızca renkle taşınmaz: rozet METİN yazar) ─────────────
export function UnitStateBadge({
  state,
  faultCode,
}: {
  state: WashUnitState | null;
  faultCode?: string | null;
}): ReactElement {
  const { t } = useTranslation();
  if (state === null)
    return (
      <StatusBadge tone="neutral" className="whitespace-nowrap">
        {t("wash.units.noState")}
      </StatusBadge>
    );
  return (
    <StatusBadge tone={UNIT_STATE_TONE[state]} className="whitespace-nowrap">
      {WASH_UNIT_STATE_LABELS[state]}
      {state === "FAULT" && faultCode ? ` · ${faultCode}` : ""}
    </StatusBadge>
  );
}

export function ActivationStatusBadge({ status }: { status: WashActivationStatus }): ReactElement {
  return (
    <StatusBadge tone={ACTIVATION_STATUS_TONE[status]} className="whitespace-nowrap">
      {WASH_ACTIVATION_STATUS_LABELS[status]}
    </StatusBadge>
  );
}

export function CycleStatusBadge({ status }: { status: WashCycleStatus }): ReactElement {
  return (
    <StatusBadge tone={CYCLE_STATUS_TONE[status]} className="whitespace-nowrap">
      {WASH_CYCLE_STATUS_LABELS[status]}
    </StatusBadge>
  );
}

export function CreditStatusBadge({ status }: { status: WashCreditStatus }): ReactElement {
  return (
    <StatusBadge tone={CREDIT_STATUS_TONE[status]} className="whitespace-nowrap">
      {WASH_CREDIT_STATUS_LABELS[status]}
    </StatusBadge>
  );
}

export function AppointmentStatusBadge({ status }: { status: WashAppointmentStatus }): ReactElement {
  return (
    <StatusBadge tone={APPOINTMENT_STATUS_TONE[status]} className="whitespace-nowrap">
      {WASH_APPOINTMENT_STATUS_LABELS[status]}
    </StatusBadge>
  );
}

export function WorkOrderStatusBadge({ status }: { status: WashWorkOrderStatus }): ReactElement {
  return (
    <StatusBadge tone={WORK_ORDER_STATUS_TONE[status]} className="whitespace-nowrap">
      {WASH_WORK_ORDER_STATUS_LABELS[status]}
    </StatusBadge>
  );
}

export function ReadySmsBadge({ status }: { status: WashReadySmsStatus }): ReactElement {
  return (
    <StatusBadge tone={READY_SMS_TONE[status]} className="whitespace-nowrap">
      {WASH_READY_SMS_STATUS_LABELS[status]}
    </StatusBadge>
  );
}

/** Tanım listesi satırı (`<dl>` içinde). */
export function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}): ReactElement {
  return (
    <div className={`grid gap-0.5 ${className ?? ""}`}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  );
}

/** Yatay kayan tablo kabı: klavyeyle odaklanır (sağdaki sütunlara ulaşılır), ekran okuyucuya adıyla duyurulur. */
export function TableFrame({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className="overflow-x-auto rounded-lg border" tabIndex={0} role="group" aria-label={label}>
      <table className="w-full text-sm">
        <caption className="sr-only">{label}</caption>
        {children}
      </table>
    </div>
  );
}

export function Th({
  children,
  align = "left",
  className,
}: {
  children: ReactNode;
  align?: "left" | "right" | "center";
  className?: string;
}): ReactElement {
  return (
    <th
      scope="col"
      className={`px-3 py-2 ${align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"} ${className ?? ""}`}
    >
      {children}
    </th>
  );
}
